import { useId } from 'react'
import { CheckCircle2, Plus, Trash2, Zap } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AppraisalField } from '@/components/deals/appraisal/AppraisalField'
import { MarketEvidenceUpdate } from '@/components/deals/appraisal/MarketEvidenceUpdate'
import {
  APPRAISAL_OPEX_DEFAULTS,
  INITIAL_HMO_ROOMS,
} from '@/components/deals/appraisal/appraisal-data'
import {
  calculateHmoSummary,
  formatNumber,
  parseNumber,
} from '@/components/deals/appraisal/appraisal-calculations'
import { marketInputGuidance } from '@/components/deals/appraisal/appraisal-market-guidance'
import { inputTargetPath } from '@/components/deals/appraisal/appraisal-market-data'
import { cn } from '@/lib/utils'

const DEFAULT_STATE = {
  rooms: INITIAL_HMO_ROOMS,
  valuationMethod: 'HMO Comparable',
  propertyType: 'Flat',
  constructionDate: 'Pre 1914',
  finishQuality: 'Very High',
  outdoorSpace: 'None',
  hasLivingRoom: true,
  hasParking: false,
  hmoSuggestedValue: '',
  hmoAdoptedValue: '',
  suggestedSqft: '',
  adoptedSqft: '',
  internalArea: '',
  adoptedEndValue: '',
  opexPercent: APPRAISAL_OPEX_DEFAULTS.hmo,
  yieldPercent: '8',
}

const ROOM_TYPES = ['Double Ensuite', 'Double Shared Bath', 'Single Ensuite', 'Single Shared Bath']
const VALUATION_METHODS = ['HMO Comparable', 'Bricks & Mortar (£/sqft)', 'Commercial']
const PERSISTED_STATE_FIELDS = [
  'rooms',
  'valuationMethod',
  'propertyType',
  'constructionDate',
  'finishQuality',
  'outdoorSpace',
  'hasLivingRoom',
  'hasParking',
  'hmoSuggestedValue',
  'hmoAdoptedValue',
  'suggestedSqft',
  'adoptedSqft',
  'internalArea',
  'adoptedEndValue',
  'opexPercent',
  'yieldPercent',
]

export function HmoScheme({
  state,
  exitMode = 'Hold',
  postcodeArea,
  showValidationErrors,
  onStateChange,
  changedEvidence = {},
  onUseLatestEvidence,
  onKeepAdoptedEvidence,
}) {
  const schemeState = { ...DEFAULT_STATE, ...persistentState(state) }
  const rooms = Array.isArray(schemeState.rooms) ? schemeState.rooms : INITIAL_HMO_ROOMS
  const summary = calculateHmoSummary(
    rooms,
    schemeState.opexPercent,
    schemeState.yieldPercent,
  )
  const schemeSummary = hmoSchemeSummary(schemeState, summary)

  function updateState(patch) {
    onStateChange?.({ ...schemeState, rooms, ...patch })
  }

  function updateRoom(roomId, field, value) {
    updateState({
      rooms: rooms.map((room) => (
        room.id === roomId ? { ...room, [field]: value } : room
      )),
    })
  }

  function addRoom() {
    const nextId = Math.max(...rooms.map((room) => Number(room.id) || 0), 0) + 1
    updateState({
      rooms: [
        ...rooms,
        {
          id: nextId,
          name: `Room ${nextId}`,
          type: 'Double Ensuite',
          suggestedRent: '',
          adoptedRent: '',
        },
      ],
    })
  }

  function removeRoom(roomId) {
    updateState({ rooms: rooms.filter((room) => room.id !== roomId) })
  }

  function updateField(field) {
    return (event) => updateState({ [field]: event.target.value })
  }

  function marketGuidance(suggestedValue, adoptedValue) {
    return marketInputGuidance({
      suggestedValue,
      adoptedValue,
      postcodeArea,
      showValidationErrors,
    })
  }

  function evidenceUpdate(field, rowId) {
    return changedEvidence[inputTargetPath({ scheme: 'hmo', field, rowId })]
  }

  function evidenceNotice(field, rowId) {
    return (
      <MarketEvidenceUpdate
        update={evidenceUpdate(field, rowId)}
        onUseLatest={onUseLatestEvidence}
        onKeepCurrent={onKeepAdoptedEvidence}
      />
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/50 pb-2">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            1. Room Schedule
          </h3>
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={addRoom}
            className="uppercase tracking-widest text-primary hover:text-primary"
          >
            <Plus aria-hidden="true" className="h-3.5 w-3.5" />
            Add Room
          </Button>
        </div>

        <div className="space-y-3">
          <div className={cn(
            'grid gap-3 px-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground',
            exitMode === 'Hold'
              ? 'grid-cols-[minmax(5rem,1fr)_minmax(10rem,2fr)_minmax(6rem,1fr)_minmax(6rem,1fr)_2rem]'
              : 'grid-cols-[minmax(5rem,1fr)_minmax(10rem,2fr)_2rem]',
          )}>
            <div>Room ID</div>
            <div>Room Type</div>
            {exitMode === 'Hold' ? <div>Suggested</div> : null}
            {exitMode === 'Hold' ? <div className="text-primary">Adopted *</div> : null}
            <div aria-hidden="true" />
          </div>

          {rooms.map((room, index) => {
            const rowLabel = `Room ${index + 1}`

            return (
              <div
                key={room.id}
                className={cn(
                  'grid items-center gap-3 rounded-lg border border-border/60 bg-background/60 p-2 transition-colors hover:border-border',
                  exitMode === 'Hold'
                    ? 'grid-cols-[minmax(5rem,1fr)_minmax(10rem,2fr)_minmax(6rem,1fr)_minmax(6rem,1fr)_2rem]'
                    : 'grid-cols-[minmax(5rem,1fr)_minmax(10rem,2fr)_2rem]',
                )}
              >
                <FieldInput
                  ariaLabel={`${rowLabel} name`}
                  value={room.name}
                  onChange={(value) => updateRoom(room.id, 'name', value)}
                />
                <LabeledSelect
                  label={`${rowLabel} type`}
                  value={room.type}
                  options={ROOM_TYPES}
                  onValueChange={(value) => updateRoom(room.id, 'type', value)}
                  isLabelHidden
                />
                {exitMode === 'Hold' ? <div className="flex items-center gap-1.5 px-2 text-xs font-medium text-muted-foreground">
                  <Zap aria-hidden="true" className="h-3 w-3 text-primary" />
                  {suggestedCurrency(room.suggestedRent)}
                </div> : null}
                {exitMode === 'Hold' ? <div className="space-y-2">
                  <AppraisalField
                    label={`${rowLabel} adopted rent`}
                    value={room.adoptedRent}
                    prefix="£"
                    inputMode="decimal"
                    aria-label={`${rowLabel} adopted rent`}
                    {...marketGuidance(room.suggestedRent, room.adoptedRent)}
                    className="h-8 text-xs font-bold"
                    isLabelHidden
                    onChange={(event) => updateRoom(room.id, 'adoptedRent', event.target.value)}
                  />
                  {evidenceNotice('adoptedRent', room.id)}
                </div> : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remove ${rowLabel}`}
                  disabled={rooms.length === 1}
                  onClick={() => removeRoom(room.id)}
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 aria-hidden="true" className="h-4 w-4" />
                </Button>
              </div>
            )
          })}
        </div>

        {exitMode === 'Hold' ? <div className="flex flex-wrap gap-5 pt-3">
          <SummaryBox label="Gross Monthly Rent" value={`£${formatNumber(summary.grossMonthlyRent)}`} />
          <SummaryBox label="Gross Annual Rent" value={`£${formatNumber(summary.grossAnnualRent)}`} />
        </div> : null}
      </div>

      <div className="space-y-4 pt-2">
        <h3 className="border-b border-border/50 pb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          2. Valuation
        </h3>
        <div className="space-y-5">
          <div className="md:w-1/3">
            <LabeledSelect
              label="Valuation Method"
              value={schemeState.valuationMethod}
              options={VALUATION_METHODS}
              onValueChange={(value) => updateState({ valuationMethod: value })}
            />
          </div>

          {schemeState.valuationMethod === 'HMO Comparable' ? (
            <HmoComparableFields
              state={schemeState}
              marketGuidance={marketGuidance}
              onUpdate={updateState}
              onUpdateField={updateField}
              evidenceNotice={evidenceNotice}
            />
          ) : null}

          {schemeState.valuationMethod === 'Bricks & Mortar (£/sqft)' ? (
            <BricksAndMortarFields
              state={schemeState}
              marketGuidance={marketGuidance}
              onUpdateField={updateField}
              evidenceNotice={evidenceNotice}
            />
          ) : null}

          {schemeState.valuationMethod === 'Commercial' ? (
            <CommercialValuationFields
              state={schemeState}
              summary={summary}
              showIncome={exitMode === 'Hold'}
              onUpdateField={updateField}
            />
          ) : null}
        </div>

        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-foreground">Scheme Summary</h4>
                <p className="text-xs font-medium text-muted-foreground">
                  Adopted values driving the appraisal
                </p>
              </div>
            </div>
            <dl className="flex flex-wrap gap-6 text-right">
              <SummaryItem label={schemeSummary.valueLabel} value={schemeSummary.value} />
              {exitMode === 'Hold' ? (
                <SummaryItem label={schemeSummary.incomeLabel} value={schemeSummary.income} />
              ) : null}
            </dl>
          </div>
        </div>
      </div>
    </div>
  )
}

function HmoComparableFields({ state, marketGuidance, onUpdate, onUpdateField, evidenceNotice }) {
  return (
    <div className="space-y-5">
      <div className="grid gap-5 md:grid-cols-3">
        <LabeledSelect
          label="Property Type"
          value={state.propertyType}
          options={['Flat', 'Detached House', 'Terraced House', 'Semi-Detached House']}
          onValueChange={(value) => onUpdate({ propertyType: value })}
        />
        <LabeledSelect
          label="Construction Date"
          value={state.constructionDate}
          options={['Pre 1914', '1914-2000', '2000 onwards']}
          onValueChange={(value) => onUpdate({ constructionDate: value })}
        />
        <LabeledSelect
          label="Finish Quality"
          value={state.finishQuality}
          options={['Very High', 'High', 'Average', 'Below Average', 'Unmodernised']}
          onValueChange={(value) => onUpdate({ finishQuality: value })}
        />
        <LabeledSelect
          label="Outdoor Space"
          value={state.outdoorSpace}
          options={['None', 'Balcony / Terrace', 'Garden', 'Very Large Garden']}
          onValueChange={(value) => onUpdate({ outdoorSpace: value })}
        />
        <ToggleField
          label="Living Room"
          checked={Boolean(state.hasLivingRoom)}
          onChange={(checked) => onUpdate({ hasLivingRoom: checked })}
        />
        <ToggleField
          label="Parking"
          checked={Boolean(state.hasParking)}
          onChange={(checked) => onUpdate({ hasParking: checked })}
        />
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <Button
            type="button"
            variant="outline"
            className="w-full uppercase tracking-widest text-primary hover:text-primary"
          >
            <Zap aria-hidden="true" className="h-3.5 w-3.5" />
            Scan Local HMOs
          </Button>
        </div>
        <ReadOnlyValue
          label="Suggested HMO Value"
          value={suggestedCurrency(state.hmoSuggestedValue)}
          icon={Zap}
        />
        <div className="space-y-2">
          <AppraisalField
            label="Adopted HMO Value *"
            value={state.hmoAdoptedValue}
            prefix="£"
            inputMode="decimal"
            {...marketGuidance(state.hmoSuggestedValue, state.hmoAdoptedValue)}
            onChange={onUpdateField('hmoAdoptedValue')}
          />
          {evidenceNotice('hmoAdoptedValue')}
        </div>
      </div>
    </div>
  )
}

function BricksAndMortarFields({ state, marketGuidance, onUpdateField, evidenceNotice }) {
  const calculatedValue = parseNumber(state.adoptedSqft) * parseNumber(state.internalArea)

  return (
    <div className="grid gap-5 md:grid-cols-3">
      <ReadOnlyValue label="Suggested £/sqft" value={suggestedCurrency(state.suggestedSqft)} icon={Zap} />
      <div className="space-y-2">
        <AppraisalField
          label="Adopted £/sqft *"
          value={state.adoptedSqft}
          prefix="£"
          inputMode="decimal"
          {...marketGuidance(state.suggestedSqft, state.adoptedSqft)}
          onChange={onUpdateField('adoptedSqft')}
        />
        {evidenceNotice('adoptedSqft')}
      </div>
      <AppraisalField
        label="Internal Area *"
        value={state.internalArea}
        suffix="sqft"
        inputMode="decimal"
        onChange={onUpdateField('internalArea')}
      />
      <ReadOnlyValue label="Calculated End Value" value={`£${formatNumber(calculatedValue)}`} />
    </div>
  )
}

function CommercialValuationFields({ state, summary, showIncome, onUpdateField }) {
  return (
    <div className="grid gap-5 md:grid-cols-3">
      {showIncome ? <ReadOnlyValue
        label="Gross Annual Rent"
        value={`£${formatNumber(summary.grossAnnualRent)}`}
        icon={Zap}
      /> : null}
      {showIncome ? <AppraisalField
        label="OPEX *"
        value={state.opexPercent}
        suffix="%"
        inputMode="decimal"
        onChange={onUpdateField('opexPercent')}
      /> : null}
      {showIncome ? <ReadOnlyValue
        label="Net Operating Income"
        value={`£${formatNumber(summary.netOperatingIncome)}`}
      /> : null}
      <AppraisalField
        label="Adopted Yield % *"
        value={state.yieldPercent}
        suffix="%"
        inputMode="decimal"
        onChange={onUpdateField('yieldPercent')}
      />
      <div className="md:col-span-2">
        <ReadOnlyValue
          label="Resulting Commercial Value"
          value={`£${formatNumber(summary.commercialValue)}`}
        />
      </div>
    </div>
  )
}

function FieldInput({ ariaLabel, value, onChange }) {
  const inputId = useId()

  return (
    <div>
      <Label htmlFor={inputId} className="sr-only">
        {ariaLabel}
      </Label>
      <Input
        id={inputId}
        value={value}
        aria-label={ariaLabel}
        className="h-8 border-transparent bg-transparent px-2 text-sm font-bold shadow-none"
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

function LabeledSelect({ label, ariaLabel, value, options, onValueChange, isLabelHidden = false }) {
  const selectId = useId()

  return (
    <div className="space-y-1.5">
      {isLabelHidden ? (
        <span id={`${selectId}-label`} className="sr-only">
          {label}
        </span>
      ) : (
        <Label
          htmlFor={selectId}
          className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
        >
          {label}
        </Label>
      )}
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          id={selectId}
          className="w-full"
          aria-label={isLabelHidden ? undefined : ariaLabel ?? label}
          aria-labelledby={isLabelHidden ? `${selectId}-label` : undefined}
        >
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function ToggleField({ label, checked, onChange }) {
  const switchId = useId()

  return (
    <div className="space-y-1.5">
      <Label
        htmlFor={switchId}
        className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
      >
        {label}
      </Label>
      <div className="flex items-center gap-2 pt-2">
        <button
          id={switchId}
          type="button"
          role="switch"
          aria-checked={checked}
          onClick={() => onChange(!checked)}
          className={cn(
            'relative inline-flex h-5 w-9 items-center rounded-full border transition-colors',
            checked ? 'border-primary bg-primary' : 'border-border bg-muted',
          )}
        >
          <span
            className={cn(
              'inline-block h-4 w-4 rounded-full bg-background shadow-sm transition-transform',
              checked ? 'translate-x-[18px]' : 'translate-x-[2px]',
            )}
          />
        </button>
        <span className="text-sm font-medium text-foreground">
          {checked ? 'Yes' : 'No'}
        </span>
      </div>
    </div>
  )
}

function ReadOnlyValue({ label, value, icon: Icon }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="flex min-h-9 items-center gap-2 rounded-lg border border-border/60 bg-background/60 px-3 py-2 text-sm font-medium text-muted-foreground">
        {Icon ? <Icon aria-hidden="true" className="h-3.5 w-3.5 text-primary" /> : null}
        {value}
      </p>
    </div>
  )
}

function SummaryBox({ label, value }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="rounded-lg border border-border/60 bg-background/60 px-3 py-2.5 text-sm font-bold text-foreground">
        {value}
      </p>
    </div>
  )
}

function SummaryItem({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-lg font-semibold text-foreground">{value}</dd>
    </div>
  )
}

function suggestedCurrency(value) {
  return value ? `£${value}` : 'Unavailable'
}

function hmoSchemeSummary(state, summary) {
  if (state.valuationMethod === 'Bricks & Mortar (£/sqft)') {
    return {
      valueLabel: 'End Value / GDV',
      value: `£${formatNumber(parseNumber(state.adoptedSqft) * parseNumber(state.internalArea))}`,
      incomeLabel: 'Gross Monthly',
      income: `£${formatNumber(summary.grossMonthlyRent)}`,
    }
  }

  if (state.valuationMethod === 'Commercial') {
    return {
      valueLabel: 'Commercial Value',
      value: `£${formatNumber(summary.commercialValue)}`,
      incomeLabel: 'Net Operating Income',
      income: `£${formatNumber(summary.netOperatingIncome)}`,
    }
  }

  return {
    valueLabel: 'HMO Value',
    value: `£${state.hmoAdoptedValue}`,
    incomeLabel: 'Gross Monthly',
    income: `£${formatNumber(summary.grossMonthlyRent)}`,
  }
}

function persistentState(state) {
  if (!state) return {}

  const persistedState = {}
  PERSISTED_STATE_FIELDS.forEach((field) => {
    if (state[field] !== undefined) persistedState[field] = state[field]
  })

  return persistedState
}
